import { z } from "zod";

/**
 * Matches a calendar day with no time component: `YYYY-MM-DD`.
 */
export const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Determines whether a `YYYY-MM-DD` string is a real calendar date.
 *
 * The regex alone accepts shape-valid but non-existent dates such as
 * `2026-13-01` or `2026-02-30`. This parses the parts and confirms they
 * round-trip through a UTC `Date` unchanged, rejecting month/day overflow.
 *
 * @param value - A `YYYY-MM-DD` string (assumed to already match the shape).
 * @returns True when the value is an existing calendar date.
 */
export function isRealCalendarDate(value: string): boolean {
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 * Zod schema for a date-only string in `YYYY-MM-DD` form. Used for columns
 * that are semantically a calendar day (e.g. quote expiration) and are stored
 * as a Postgres `DATE`. Unlike `z.coerce.date()`, this carries no time or
 * timezone, so it cannot be shifted off-by-one across timezones.
 *
 * Validates both the shape and that the value is a real calendar date, so
 * invalid dates (e.g. `2026-13-01`) are rejected at the API boundary rather
 * than falling through to a Postgres `DATE` insert error.
 */
export const DateOnlyStringSchema = z
  .string()
  .regex(DATE_ONLY_REGEX, "Must be a date in YYYY-MM-DD format")
  .refine(isRealCalendarDate, "Must be a valid calendar date");

/**
 * A date-only string in `YYYY-MM-DD` form (no time, no timezone).
 */
export type DateOnlyString = z.infer<typeof DateOnlyStringSchema>;
